<template>
    <DatePickerRoot
        v-model="model"
        v-model:placeholder="referenceDate"
        v-model:open="open"
        :locale="locale"
        :min-value="minValue"
        :max-value="maxValue"
        :is-date-unavailable="isDateUnavailable"
        :is-date-disabled="isDateDisabled"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :number-of-months="numberOfMonths"
        :paged-navigation="pagedNavigation"
        :week-starts-on="weekStartsOn"
        :weekday-format="weekdayFormat"
        :fixed-weeks="fixedWeeks"
        :prevent-deselect="preventDeselect"
        :close-on-select="closeOnSelect"
        :modal="modal"
        granularity="day"
    >
        <template #default>
            <!-- The popover anchors to the FIELD, not the trigger button. Anchored to the
                trigger it would drop from the small leading icon; DatePickerAnchor with
                as-child makes the field root the popper reference, so the calendar drops
                cleanly under the full-width field, left-aligned. -->
            <DatePickerAnchor as-child>
                <!-- DatePickerRoot renders no element and drops `$attrs` (its template is
                    `PopoverRoot` + a slot, `inheritAttrs: false`), so the block class, the id
                    and every aria hook live on DatePickerField — the `role="group"` element.
                    KToggleGroup does the opposite; the disagreement is worth a comment. -->
                <DatePickerField
                    :id="controlId"
                    :name="name"
                    :class="[classes, $attrs.class]"
                    :style="$attrs.style"
                    :aria-labelledby="labelId"
                    :aria-describedby="describedBy"
                    :aria-invalid="invalid || undefined"
                    v-bind="rootAttrs"
                >
                    <template #default="{ segments }">
                        <div :class="element('box')">
                            <!-- Keyed by index, not item.part: every literal shares
                            part='literal', so keying by part gives duplicate keys and Vue
                            patches the wrong nodes when the locale changes. -->
                            <template
                                v-for="(item, index) in segments"
                                :key="index"
                            >
                                <span
                                    v-if="item.part === 'literal'"
                                    :class="element('literal')"
                                    aria-hidden="true"
                                >{{ item.value }}</span>
                                <DatePickerInput
                                    v-else
                                    ref="segmentRefs"
                                    :part="item.part"
                                    :class="element('segment')"
                                >
                                    {{ item.value }}
                                </DatePickerInput>
                            </template>

                            <!-- The open control: a calendar icon button. It renders
                                visually at the LEADING edge (CSS `order`) but stays LAST in
                                the DOM on purpose — so Tab into the field lands on the first
                                segment for immediate typing, and reka's ArrowRight from the
                                year segment reaches this trigger. Clicking it opens the
                                calendar; the segments stay pure text entry, so manual typing
                                is never interrupted by the popover. `@pointerdown`/`@keydown`
                                record the open modality for `onOpenAutoFocus`. -->
                            <DatePickerTrigger
                                :class="element('trigger')"
                                :aria-label="triggerLabel"
                                @pointerdown="openedWithPointer = true"
                                @keydown="openedWithPointer = false"
                            >
                                <slot
                                    name="trigger-icon"
                                    :open="open"
                                >
                                    <svg
                                        viewBox="0 0 20 20"
                                        aria-hidden="true"
                                    >
                                        <rect
                                            x="2.5"
                                            y="4"
                                            width="15"
                                            height="13.5"
                                            rx="2"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                        />
                                        <path
                                            d="M2.5 8h15M6.5 2v3M13.5 2v3"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                        />
                                    </svg>
                                </slot>
                            </DatePickerTrigger>
                        </div>
                    </template>
                </DatePickerField>
            </DatePickerAnchor>

            <DatePickerContent
                :class="[cBlock(), $attrs['content-class']]"
                :side="side"
                :align="align"
                :side-offset="4"
                @open-auto-focus="onOpenAutoFocus"
            >
                <slot name="content">
                    <!-- `calendar-label` is NOT forwarded by DatePickerCalendar from the
                        root context — it arrives as a fallthrough attribute on this vnode,
                        which Vue merges into CalendarRoot's props before resolution. This
                        is load-bearing (reka's built-in default is the hardcoded English
                        'Event Date') and pinned by a test; do not "clean it up". -->
                    <DatePickerCalendar
                        :class="cElement('calendar')"
                        :calendar-label="calendarLabel"
                    >
                        <template #default="{ weekDays, grid }">
                            <!-- The header renders one shared nav + heading only when a
                                single month is showing. With two months, the nav moves to
                                the outer edges and each month gets its own heading inside
                                its section — otherwise "December 2026 - January 2027"
                                would sit over two grids that each need their own label. -->
                            <CalendarHeader :class="cElement('header')">
                                <CalendarPrev :class="cElement('nav')">
                                    <slot name="prev-icon">
                                        <svg
                                            viewBox="0 0 20 20"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M12.5 4.5L7 10l5.5 5.5"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="1.75"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </slot>
                                </CalendarPrev>
                                <!-- CalendarHeading is ONE combined label ("December 2026" or
                                    "December 2026 - January 2027"); it only renders for a single
                                    month — stacked months label themselves inside their sections. -->
                                <CalendarHeading
                                    v-if="grid.length === 1"
                                    :class="cElement('heading')"
                                >
                                    <template #default="{ headingValue }">
                                        <slot
                                            name="heading"
                                            :heading-value="headingValue"
                                        >
                                            {{ headingValue }}
                                        </slot>
                                    </template>
                                </CalendarHeading>
                                <div
                                    v-else
                                    :class="cElement('heading')"
                                    aria-hidden="true"
                                />
                                <CalendarNext :class="cElement('nav')">
                                    <slot name="next-icon">
                                        <svg
                                            viewBox="0 0 20 20"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M7.5 4.5L13 10l-5.5 5.5"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="1.75"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </slot>
                                </CalendarNext>
                            </CalendarHeader>

                            <div :class="cElement('months')">
                                <!-- One section per visible month. The per-month heading is
                                    a formatted month name (formatter.custom on the month's
                                    first day) — simpler and more honest than reka's
                                    combined "December 2026 - January 2027" heading, which
                                    only makes sense when the months are the single visible
                                    page. Each section repeats the weekday header, keeping
                                    the columns self-describing. -->
                                <div
                                    v-for="month in grid"
                                    :key="month.value.toString()"
                                    :class="cElement('month')"
                                >
                                    <CalendarHeading
                                        v-if="grid.length > 1"
                                        :class="cElement('month-heading')"
                                    >
                                        <template #default="{ headingValue }">
                                            <slot
                                                name="heading"
                                                :heading-value="headingValue"
                                            >
                                                {{ monthHeading(month) }}
                                            </slot>
                                        </template>
                                    </CalendarHeading>
                                    <CalendarGrid :class="cElement('grid')">
                                        <CalendarGridHead :class="cElement('grid-head')">
                                            <CalendarGridRow :class="cElement('grid-row')">
                                                <CalendarHeadCell
                                                    v-for="day in weekDays"
                                                    :key="day"
                                                    :class="cElement('head-cell')"
                                                >
                                                    {{ day }}
                                                </CalendarHeadCell>
                                            </CalendarGridRow>
                                        </CalendarGridHead>
                                        <CalendarGridBody :class="cElement('grid-body')">
                                            <CalendarGridRow
                                                v-for="(weekDates, weekIndex) in month.rows"
                                                :key="weekIndex"
                                                :class="cElement('grid-row')"
                                            >
                                                <CalendarCell
                                                    v-for="weekDate in weekDates"
                                                    :key="weekDate.toString()"
                                                    :date="weekDate"
                                                    :class="cElement('cell')"
                                                >
                                                    <CalendarCellTrigger
                                                        :day="weekDate"
                                                        :month="month.value"
                                                        :class="cElement('cell-trigger')"
                                                    >
                                                        <template #default="{ dayValue, disabled: dayDisabled, today, selected, outsideView, unavailable }">
                                                            <slot
                                                                name="day"
                                                                :date="weekDate"
                                                                :day-value="dayValue"
                                                                :today="today"
                                                                :selected="selected"
                                                                :disabled="dayDisabled"
                                                                :unavailable="unavailable"
                                                                :outside-view="outsideView"
                                                            >
                                                                {{ dayValue }}
                                                            </slot>
                                                        </template>
                                                    </CalendarCellTrigger>
                                                </CalendarCell>
                                            </CalendarGridRow>
                                        </CalendarGridBody>
                                    </CalendarGrid>
                                </div>
                            </div>

                            <div
                                v-if="$slots.footer"
                                :class="cElement('footer')"
                            >
                                <slot
                                    name="footer"
                                    :close="close"
                                />
                            </div>
                        </template>
                    </DatePickerCalendar>
                </slot>
            </DatePickerContent>
        </template>
    </DatePickerRoot>
</template>

<script setup lang="ts">
import type { CalendarDate, DateValue } from '@internationalized/date'
import type { KDatePickerProps } from './types'

import { DateFormatter } from '@internationalized/date'
import useBem from '@yeeroen/use-bem'
import {
    CalendarCell,
    CalendarCellTrigger,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHead,
    CalendarGridRow,
    CalendarHeadCell,
    CalendarHeader,
    CalendarHeading,
    CalendarNext,
    CalendarPrev,
    DatePickerAnchor,
    DatePickerCalendar,
    DatePickerContent,
    DatePickerField,
    DatePickerInput,
    DatePickerRoot,
    DatePickerTrigger,
} from 'reka-ui'
import { computed, nextTick, ref, useAttrs, useTemplateRef } from 'vue'
import { useFieldControl } from '../field/use-field-control'

defineOptions({ name: 'KDatePicker', inheritAttrs: false })

const props = withDefaults(defineProps<KDatePickerProps>(), {
    fullWidth: true,
    readonly: false,
    numberOfMonths: 1,
    pagedNavigation: false,
    weekdayFormat: 'narrow',
    // karibu flips reka's `false` default on both of these — a jumping popover and a
    // popover that stays open after the only decision are both worse for a single date.
    fixedWeeks: true,
    closeOnSelect: true,
    preventDeselect: false,
    modal: false,
    triggerLabel: 'Choose date',
    side: 'bottom',
    align: 'start',
    // Left undefined on purpose: `useFieldControl` falls back to the KField context with
    // `??`, and a `false` default would win over an enclosing disabled field.
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    /** The calendar glyph inside the field. karibu ships no icons, so the default is a small inline SVG and every real call site fills this. */
    'trigger-icon'?: (props: { open: boolean }) => unknown
    /** Month navigation glyphs. Inline SVG fallbacks for the same reason. */
    'prev-icon'?: () => unknown
    'next-icon'?: () => unknown
    /** Replaces "December 2026". This is where a month/year dropdown would go. */
    'heading'?: (props: { headingValue: string }) => unknown
    /** Per-cell content — booking apps put a price or a dot under the number. */
    'day'?: (props: { date: DateValue, dayValue: string, today: boolean, selected: boolean, disabled: boolean, unavailable: boolean, outsideView: boolean }) => unknown
    /** Below the grid, inside the popover — "Today" / "Clear" buttons live here. */
    'footer'?: (props: { close: () => void }) => unknown
    /** Replaces the entire popover body. The escape hatch; using it means owning the calendar markup. */
    'content'?: () => unknown
}>()

const model = defineModel<CalendarDate | undefined>({ default: undefined })
const referenceDate = defineModel<DateValue | undefined>('referenceDate', { default: undefined })
const open = defineModel<boolean>('open', { default: false })

const attrs = useAttrs()
const { block, element, modifier } = useBem('k-date-picker')
// The popup lives in a portal, so it is a second BEM block, not `.k-date-picker__content`
// — an element class would promise a DOM parent that is not there.
const { block: cBlock, element: cElement } = useBem('k-date-picker-content')

const { controlId, labelId, describedBy, invalid, disabled, required, size } = useFieldControl(props)

// class/style land on the field (`role="group"`) — a caller adding a class means the box,
// not a segment. `id` is stripped: it's driven by the field context and forwarded
// explicitly to reka's `id` prop, so it lands on the hidden input rather than on the div.
const rootAttrs = computed(() => {
    const { id: _id, class: _class, style: _style, 'content-class': _contentClass, ...rest } = attrs

    return rest
})

const classes = computed(() => block(
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
    modifier('readonly', props.readonly),
    modifier('full-width', props.fullWidth),
))

// ref="segmentRefs" inside the v-for collects each rendered DatePickerInput in
// declaration order (the v-for is keyed by index, tracking the segments array), so
// index 0 is the first editable segment. Used by focus() to land there.
const segmentRefs = useTemplateRef<{ $el: HTMLElement }[]>('segmentRefs')

function close() {
    open.value = false
}

// Whether the popover was just opened with the pointer rather than the keyboard. The
// trigger's `@pointerdown` sets it, its `@keydown` clears it; a plain ref, not reactive
// — `openAutoFocus` below is emitted synchronously during the open render, before a
// watcher could flush.
const openedWithPointer = ref(false)

// Focus goes into the calendar only for a keyboard open. For a pointer open the day the
// user wants is already under the cursor, and dragging focus to the grid (with its
// focus-visible ring) reads as the popover fighting the mouse. Keyboard activation keeps
// reka's priority — selected, then today, then the first enabled day — which we re-run
// here after cancelling reka's unconditional `handleCalendarInitialFocus`. react-aria
// does the same split (= APG without punishing pointer users).
async function onOpenAutoFocus(event: Event) {
    event.preventDefault()

    if (openedWithPointer.value) {
        // The popover mounts across two microtasks (the dismissable layer, then the
        // calendar's Presence/content); a pointer open must suppress BOTH emitted
        // openAutoFocus events, the first of which fires before the grid exists. Wait
        // for the grid to mount before clearing the flag, or the second event fires
        // after the reset and focuses the grid anyway.
        await nextTick()

        openedWithPointer.value = false

        return
    }

    // Keyboard open: refocus the day on the same deferred schedule, once the grid has
    // rendered. reka's own handleCalendarInitialFocus uses the same priority.
    await nextTick()

    const target = event.target as HTMLElement | null

    const cell = target?.querySelector<HTMLElement>('[data-selected]')
        ?? target?.querySelector<HTMLElement>('[data-today]')
        ?? target?.querySelector<HTMLElement>('[data-value]:not([data-outside-view]):not([data-disabled])')

    cell?.focus()
}

// For a single month the shared `CalendarHeading` ("December 2026") is enough; with
// several, each month needs its own short label inside its section. reka computes the
// combined heading from its own formatter but doesn't expose per-month strings, so the
// section headings are formatted here from the same resolved locale.
const monthFormatter = computed(() => new DateFormatter(props.locale ?? 'en', { month: 'long', year: 'numeric' }))

function monthHeading(month: { value: DateValue }) {
    return monthFormatter.value.format(month.value.toDate('UTC'))
}

async function focus() {
    await nextTick()
    segmentRefs.value?.[0]?.$el?.focus()
}

defineExpose({ focus })
</script>

<style lang="scss">
@use './k-date-picker.scss';
</style>
